import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import Modal from '../ui/Modal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';

interface DeathCertificate {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  fileName: string;
  fileSize: number;
  createdAt: string;
  reviewedAt?: string;
  rejectionReason?: string;
  estateOwner: {
    name: string;
    email: string;
  };
  executor: {
    name: string;
    email: string;
  };
  reviewedBy?: string;
}

const AdminDeathCerts: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  
  const [deathCerts, setDeathCerts] = useState<DeathCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  
  // Modal state
  const [selectedCert, setSelectedCert] = useState<DeathCertificate | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDeathCerts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('account-token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/death-certificates?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch death certificates');
      }
      
      const data = await response.json();
      setDeathCerts(data.deathCertificates);
    } catch (err: unknown) {
      console.error('Error fetching death certificates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isAdmin) {
      fetchDeathCerts();
    }
  }, [isAdmin, fetchDeathCerts]);

  const handleViewDocument = async (cert: DeathCertificate) => {
    const token = localStorage.getItem('account-token');
    window.open(`${API_BASE_URL}/api/admin/death-certificate/${cert.id}/file?token=${token}`, '_blank');
  };

  const handleApprove = async (cert: DeathCertificate) => {
    if (!confirm(`Are you sure you want to approve the death certificate for ${cert.estateOwner.name}'s estate?`)) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('account-token');
      const response = await fetch(`${API_BASE_URL}/api/admin/death-certificate/${cert.id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to approve death certificate');
      }
      
      fetchDeathCerts();
    } catch (err: unknown) {
      console.error('Error approving death certificate:', err);
      alert('Failed to approve: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedCert || !rejectReason.trim()) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('account-token');
      const response = await fetch(`${API_BASE_URL}/api/admin/death-certificate/${selectedCert.id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason })
      });
      
      if (!response.ok) {
        throw new Error('Failed to reject death certificate');
      }
      
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedCert(null);
      fetchDeathCerts();
    } catch (err: unknown) {
      console.error('Error rejecting death certificate:', err);
      alert('Failed to reject: ' + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Rejected</span>;
      default:
        return null;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-800 mb-2">Access Denied</h1>
          <p className="text-stone-600 mb-4">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-cyan-700 hover:text-cyan-800 text-sm font-medium flex items-center gap-2 mb-4"
          >
            ← Back to Admin Dashboard
          </button>
          <h1 className="text-3xl font-bold text-stone-800">Death Certificate Approval</h1>
          <p className="text-stone-600 mt-1">Review and process death certificate submissions</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-600">Filter:</span>
            {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-cyan-700 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchDeathCerts}
              className="mt-2 text-sm text-red-700 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-700 mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading death certificates...</p>
          </div>
        ) : deathCerts.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-stone-800 mb-1">No Death Certificates</h3>
            <p className="text-stone-600">
              {filter === 'pending' ? 'No pending death certificates to review.' : `No ${filter} death certificates found.`}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-stone-200">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Estate Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Submitted By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">File</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-stone-200">
                {deathCerts.map((cert) => (
                  <tr key={cert.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-stone-900">{cert.estateOwner.name}</div>
                        <div className="text-sm text-stone-500">{cert.estateOwner.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-stone-900">{cert.executor.name}</div>
                        <div className="text-sm text-stone-500">{cert.executor.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-stone-900">{cert.fileName}</div>
                      <div className="text-xs text-stone-500">{formatFileSize(cert.fileSize)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                      {new Date(cert.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(cert.status)}
                      {cert.status === 'rejected' && cert.rejectionReason && (
                        <div className="text-xs text-red-600 mt-1 max-w-[150px] truncate" title={cert.rejectionReason}>
                          {cert.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDocument(cert)}
                          className="text-cyan-700 hover:text-cyan-800"
                          title="View Document"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {cert.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(cert)}
                              className="text-green-600 hover:text-green-700"
                              title="Approve"
                              disabled={actionLoading}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCert(cert);
                                setShowRejectModal(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                              title="Reject"
                              disabled={actionLoading}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats Summary */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {deathCerts.filter(c => c.status === 'pending').length}
            </div>
            <div className="text-sm text-stone-600">Pending Review</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">
              {deathCerts.filter(c => c.status === 'approved').length}
            </div>
            <div className="text-sm text-stone-600">Approved</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-red-600">
              {deathCerts.filter(c => c.status === 'rejected').length}
            </div>
            <div className="text-sm text-stone-600">Rejected</div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
          setSelectedCert(null);
        }}
        title="Reject Death Certificate"
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">
            Please provide a reason for rejecting this death certificate. The executor will receive this message.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Document is unreadable, Certificate appears incomplete, Name doesn't match records..."
              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
                setSelectedCert(null);
              }}
              className="px-4 py-2 text-stone-600 hover:text-stone-800"
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={!rejectReason.trim() || actionLoading}
              className={`px-6 py-2 rounded-lg text-white font-medium ${
                !rejectReason.trim() || actionLoading
                  ? 'bg-stone-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {actionLoading ? 'Rejecting...' : 'Reject'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminDeathCerts;

